import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../utils/api';
import './FileManager.css';

interface FileInfo {
  url: string;
  type: string;
  name: string;
  storybookId: string;
  storybookTitle: string;
  size: number;
  createdAt: string;
}

const FileManager: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [recalculating, setRecalculating] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'type' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getFiles();
      setFiles(response.files || []);
      setTotalSize(response.totalSize || 0);
      setTotalFiles(response.totalFiles || 0);
    } catch (error) {
      console.error('Failed to load files:', error);
      alert('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateStorage = async () => {
    if (!window.confirm('Recalculate your storage usage? This may take a moment.')) {
      return;
    }

    try {
      setRecalculating(true);
      const response = await userAPI.recalculateStorage();
      alert(`Storage recalculated!\nOld: ${formatBytes(response.oldStorage)}\nNew: ${formatBytes(response.newStorage)}\nDifference: ${formatBytes(response.difference)}`);
      loadFiles();
    } catch (error) {
      console.error('Failed to recalculate storage:', error);
      alert('Failed to recalculate storage');
    } finally {
      setRecalculating(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'character': return '👤';
      case 'background': return '🖼️';
      case 'scene': return '🎬';
      case 'asset': return '🎨';
      case 'page-image': return '📄';
      default: return '📁';
    }
  };

  const filteredFiles = files.filter(file => 
    filterType === 'all' || file.type === filterType
  );

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    let compareA: any = a[sortBy as keyof FileInfo];
    let compareB: any = b[sortBy as keyof FileInfo];

    if (sortBy === 'date') {
      compareA = new Date(a.createdAt).getTime();
      compareB = new Date(b.createdAt).getTime();
    } else if (sortBy === 'name') {
      compareA = a.name.toLowerCase();
      compareB = b.name.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return compareA > compareB ? 1 : -1;
    } else {
      return compareA < compareB ? 1 : -1;
    }
  });

  const handleSort = (field: 'name' | 'size' | 'type' | 'date') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleViewStorybook = (storybookId: string) => {
    navigate(`/storybook/${storybookId}`);
  };

  const handleSelectFile = (fileUrl: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileUrl)) {
      newSelected.delete(fileUrl);
    } else {
      newSelected.add(fileUrl);
    }
    setSelectedFiles(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === sortedFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(sortedFiles.map(f => f.url)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedFiles.size} file(s)? This will remove them from all storybooks.`)) {
      return;
    }

    try {
      setDeleting(true);
      const response = await userAPI.deleteFiles(Array.from(selectedFiles));
      
      if (response.success) {
        alert(`Successfully deleted ${response.deletedFiles} file(s).\nFreed ${formatBytes(response.freedStorage)} of storage.${response.notFoundFiles > 0 ? `\n${response.notFoundFiles} file(s) were not found.` : ''}`);
        setSelectedFiles(new Set());
        loadFiles();
      }
    } catch (error) {
      console.error('Failed to delete files:', error);
      alert('Failed to delete files');
    } finally {
      setDeleting(false);
    }
  };

  const fileTypes = ['all', 'character', 'background', 'scene', 'asset', 'page-image'];
  const storagePercentage = Math.min((totalSize / (500 * 1024 * 1024)) * 100, 100);

  return (
    <div className="file-manager">
      <div className="file-manager-header">
        <h1>File Manager</h1>
        <button onClick={() => navigate('/dashboard')} className="btn-secondary">
          Back to Dashboard
        </button>
      </div>

      <div className="storage-overview">
        <div className="storage-info">
          <h2>Storage Usage</h2>
          <div className="storage-stats">
            <div className="stat">
              <span className="stat-label">Used:</span>
              <span className="stat-value">{formatBytes(totalSize)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total Files:</span>
              <span className="stat-value">{totalFiles}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Limit:</span>
              <span className="stat-value">500 MB</span>
            </div>
          </div>
          <div className="storage-bar">
            <div 
              className="storage-bar-fill" 
              style={{ 
                width: `${storagePercentage}%`,
                backgroundColor: storagePercentage > 90 ? '#dc3545' : storagePercentage > 70 ? '#ffc107' : '#28a745'
              }}
            />
          </div>
          <p className="storage-percentage">{storagePercentage.toFixed(1)}% used</p>
        </div>
        <div className="storage-actions">
          <button 
            onClick={handleRecalculateStorage} 
            disabled={recalculating}
            className="btn-secondary"
          >
            {recalculating ? '🔄 Recalculating...' : '🔄 Recalculate Storage'}
          </button>
        </div>
      </div>

      <div className="file-controls">
        <div className="file-filters">
          <label>Filter by type:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            {fileTypes.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Files' : type.replace('-', ' ')}
              </option>
            ))}
          </select>
        </div>
        <div className="file-count">
          Showing {sortedFiles.length} of {totalFiles} files
          {selectedFiles.size > 0 && (
            <span className="selected-count"> ({selectedFiles.size} selected)</span>
          )}
        </div>
        {selectedFiles.size > 0 && (
          <button 
            onClick={handleDeleteSelected}
            disabled={deleting}
            className="btn-danger"
          >
            {deleting ? '🗑️ Deleting...' : `🗑️ Delete Selected (${selectedFiles.size})`}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-state">
          <p>Loading files...</p>
        </div>
      ) : sortedFiles.length === 0 ? (
        <div className="empty-state">
          <p>No files found</p>
        </div>
      ) : (
        <div className="files-table-container">
          <table className="files-table">
            <thead>
              <tr>
                <th className="checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selectedFiles.size === sortedFiles.length && sortedFiles.length > 0}
                    onChange={handleSelectAll}
                    title="Select all"
                  />
                </th>
                <th onClick={() => handleSort('type')} className="sortable">
                  Type {sortBy === 'type' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('name')} className="sortable">
                  Name {sortBy === 'name' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th>Storybook</th>
                <th onClick={() => handleSort('size')} className="sortable">
                  Size {sortBy === 'size' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('date')} className="sortable">
                  Created {sortBy === 'date' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedFiles.map((file, index) => (
                <tr key={index} className={selectedFiles.has(file.url) ? 'selected' : ''}>
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedFiles.has(file.url)}
                      onChange={() => handleSelectFile(file.url)}
                    />
                  </td>
                  <td>
                    <span className="file-type-badge">
                      {getTypeIcon(file.type)} {file.type}
                    </span>
                  </td>
                  <td className="file-name">{file.name}</td>
                  <td>
                    <button 
                      onClick={() => handleViewStorybook(file.storybookId)}
                      className="link-button"
                    >
                      {file.storybookTitle}
                    </button>
                  </td>
                  <td>{formatBytes(file.size)}</td>
                  <td>{formatDate(file.createdAt)}</td>
                  <td>
                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="btn-sm">
                      View
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="file-manager-footer">
        <p className="help-text">
          💡 Tip: Delete files by removing them from your storybooks. Storage will be automatically recalculated.
        </p>
      </div>
    </div>
  );
};

export default FileManager;
